import { useState, useEffect, useRef } from 'preact/hooks';

export interface NumberRange { min: string; max: string }

interface IColumnFilterProps {
    field: string;
    values: any[];
    selected?: Set<any>;
    onFilterChange: (field: string, filterValues: Set<any>) => void;
    isOpen: boolean;
    onToggle: () => void;
    sortConfig?: { field: string | null; direction: 'asc' | 'desc' };
    onSortChange?: (field: string, direction: 'asc' | 'desc') => void;
    isNumberField?: boolean;
    range?: NumberRange;
    onRangeChange?: (field: string, range: NumberRange) => void;
}

export default function ColumnFilter({ field, values, selected, onFilterChange, isOpen, onToggle, sortConfig, onSortChange, isNumberField = false, range, onRangeChange }: IColumnFilterProps) {
    const [searchText, setSearchText] = useState('');
    // Управляемый компонент: источник правды — проп selected из родителя (columnFilters[field]).
    // Это исключает рассинхрон галочек при сбросе/перезагрузке данных.
    const selectedValues = selected ?? new Set<any>();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);

    const uniqueValues = Array.from(new Set(values.map(v => {
        const str = String(v ?? '').trim();
        return str === '' ? '(пусто)' : str;
    }))).sort();

    const filteredValues = uniqueValues.filter(val =>
        String(val).toLowerCase().includes(searchText.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                onToggle();
            }
        };

        if (isOpen && buttonRef.current) {
            const tableHeader = buttonRef.current.closest('th');
            if (tableHeader) {
                const headerRect = tableHeader.getBoundingClientRect();
                setDropdownPosition({
                    top: headerRect.top - 10,
                    left: headerRect.right - 256,
                });
            }
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        } else {
            setDropdownPosition(null);
        }
    }, [isOpen, onToggle]);

    const handleCheckboxChange = (value: any) => {
        const newSelected = new Set(selectedValues);
        if (newSelected.has(value)) { newSelected.delete(value); } else { newSelected.add(value); }
        onFilterChange(field, newSelected);
    };

    const handleSelectAll = () => {
        onFilterChange(field, new Set(filteredValues));
    };

    const handleDeselectAll = () => {
        onFilterChange(field, new Set<any>());
    };

    const hasRange = !!(range && (range.min !== '' || range.max !== ''));
    const showRange = isNumberField && !!onRangeChange;
    const hasActiveFilter = (selectedValues.size > 0 && selectedValues.size < uniqueValues.length) || hasRange;

    const setRange = (patch: Partial<NumberRange>) => {
        onRangeChange?.(field, { min: range?.min ?? '', max: range?.max ?? '', ...patch });
    };

    return (
        <>
            <button
                ref={buttonRef}
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                className={`w-4 h-4 flex items-center justify-center text-xs transition-colors ${
                    hasActiveFilter ? 'text-primary' : 'text-base-content/40 hover:text-base-content/60'
                }`}
                title="Фильтр"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
            </button>

            {isOpen && dropdownPosition && (
                <div
                    ref={dropdownRef}
                    className="fixed bg-base-100 border border-base-300 rounded-lg shadow-xl z-[100] w-64 max-h-96 flex flex-col"
                    style={{ top: `${dropdownPosition.top}px`, left: `${Math.max(10, dropdownPosition.left)}px` }}
                >
                    <div className="p-3 border-b border-base-300">
                        <input
                            type="text"
                            placeholder="Поиск..."
                            value={searchText}
                            onInput={(e) => setSearchText((e.target as HTMLInputElement).value)}
                            className="input input-bordered input-sm w-full"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    {onSortChange && (
                        <div className="p-2 border-b border-base-300">
                            <div className="text-xs font-semibold text-base-content/70 mb-2">Сортировка:</div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => onSortChange(field, 'asc')}
                                    className={`btn btn-xs flex-1 ${sortConfig?.field === field && sortConfig?.direction === 'asc' ? 'btn-primary' : 'btn-ghost'}`}
                                >
                                    {isNumberField ? 'По возрастанию' : 'От А до Я'}
                                </button>
                                <button
                                    onClick={() => onSortChange(field, 'desc')}
                                    className={`btn btn-xs flex-1 ${sortConfig?.field === field && sortConfig?.direction === 'desc' ? 'btn-primary' : 'btn-ghost'}`}
                                >
                                    {isNumberField ? 'По убыванию' : 'От Я до А'}
                                </button>
                            </div>
                        </div>
                    )}

                    {showRange && (
                        <div className="p-2 border-b border-base-300">
                            <div className="text-xs font-semibold text-base-content/70 mb-2">Диапазон (≥ / ≤):</div>
                            <div className="flex gap-2 items-center">
                                <input type="number" inputMode="decimal" placeholder="От" className="input input-bordered input-xs w-full"
                                    value={range?.min ?? ''}
                                    onClick={e => e.stopPropagation()}
                                    onInput={e => setRange({ min: (e.target as HTMLInputElement).value })} />
                                <span className="text-base-content/40">—</span>
                                <input type="number" inputMode="decimal" placeholder="До" className="input input-bordered input-xs w-full"
                                    value={range?.max ?? ''}
                                    onClick={e => e.stopPropagation()}
                                    onInput={e => setRange({ max: (e.target as HTMLInputElement).value })} />
                                {hasRange && (
                                    <button className="btn btn-xs btn-ghost" title="Сбросить диапазон"
                                        onClick={() => setRange({ min: '', max: '' })}>✕</button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="p-2 border-b border-base-300">
                        <div className="text-xs font-semibold text-base-content/70 mb-2">Фильтр:</div>
                        <div className="flex gap-2">
                            <button onClick={handleSelectAll} className="btn btn-xs btn-ghost flex-1">Выбрать все</button>
                            <button onClick={handleDeselectAll} className="btn btn-xs btn-ghost flex-1">Снять все</button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2">
                        {filteredValues.length === 0 ? (
                            <div className="text-sm text-base-content/60 text-center py-4">Ничего не найдено</div>
                        ) : (
                            <div className="space-y-1">
                                {filteredValues.map((value) => (
                                    <label key={value} className="flex items-center gap-2 p-2 hover:bg-base-200 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedValues.has(value)}
                                            onChange={() => handleCheckboxChange(value)}
                                            className="w-4 h-4 cursor-pointer"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <span className="text-sm flex-1 truncate">{value}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
